import type { GridSortModel } from "@mui/x-data-grid";
import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useApi } from "./use-api.ts";

export interface PaginationState {
  page: number;
  pageSize: number;
}

export interface SortingState {
  field: string;
  sort: "asc" | "desc" | null;
}

export interface FilterState {
  [key: string]: string | undefined;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const usePaginatedApiQuery = <T>(
  baseQuery: string,
  initialPaginationState?: PaginationState,
  initialFilters?: FilterState,
  _limit?: number,
  opts?: Omit<UseQueryOptions<T[]>, "queryKey" | "queryFn">,
) => {
  const [paginationState, setPaginationState] = useState<PaginationState>(
    initialPaginationState ?? { page: 0, pageSize: 25 },
  );

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filters, setFilters] = useState<FilterState>(initialFilters ?? {});

  const { callApi } = useApi<T[]>();

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", paginationState.pageSize.toString());
    params.set("offset", (paginationState.page * paginationState.pageSize).toString());

    // Add sorting parameters if sort model exists
    if (sortModel.length > 0) {
      const sortItem = sortModel[0];
      params.set("order_by", sortItem.field);
      params.set("order_direction", sortItem.sort || "desc");
    }

    // Add filter parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });

    const separator = baseQuery.includes("?") ? "&" : "?";
    return `${baseQuery}${separator}${params.toString()}`;
  }, [baseQuery, paginationState, sortModel, filters]);

  const queryResult = useQuery({
    queryKey: [baseQuery, paginationState, sortModel, filters],
    queryFn: async () => callApi(queryString),
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    refetchInterval: 2_000,
    ...opts,
  });

  return {
    ...queryResult,
    paginationState,
    setPaginationState,
    sortModel,
    setSortModel,
    filters,
    setFilters,
  };
};
